/**
 * Payment Method Query Hook
 * 
 * React Query hook for fetching a specific payment method by ID.
 * 
 * ## Features
 * - Type-safe query with error handling
 * - Automatic caching and refetching
 * - Loading and error states
 * - Tenant-scoped (persists across club switches)
 * 
 * @module payment/hooks
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { useTenantHandle } from '~/services/appRuntime';
import { paymentMethodService } from '~/services/internalApi/payment/api/paymentMethodService';

export interface UsePaymentMethodQueryOptions {
  /** User ID */
  userId: number | null;
  
  /** Payment method ID to fetch */
  paymentMethodId: number | null;
  
  /** Whether the query is enabled (default: true if both IDs exist) */
  enabled?: boolean;
}

/**
 * Fetch a specific payment method by ID
 * 
 * Returns the full React Query result with a convenience `paymentMethod` field.
 * 
 * @example
 * ```tsx
 * // Simple usage - convenience field
 * const { paymentMethod, isLoading } = usePaymentMethodQuery({
 *   userId: 123,
 *   paymentMethodId: 7,
 * });
 * 
 * if (isLoading) return <div>Loading...</div>;
 * 
 * return (
 *   <div>
 *     {paymentMethod ? (
 *       <span>{paymentMethod.cardBrand} •••• {paymentMethod.last4}</span>
 *     ) : (
 *       <span>Payment method not found</span>
 *     )}
 *   </div>
 * );
 * 
 * // Advanced usage - React Query features
 * const { paymentMethod, refetch, isStale } = usePaymentMethodQuery({...});
 * ```
 */
export function usePaymentMethodQuery({
  userId,
  paymentMethodId,
  enabled = true,
}: UsePaymentMethodQueryOptions) {
  const [tenantHandle] = useTenantHandle();
  
  const query = useQuery({
    // Use tenant-scoped query key (payment methods shared across clubs)
    queryKey: tenantHandle && userId && paymentMethodId
      ? queryKeys.tenant.paymentMethod(tenantHandle, userId, paymentMethodId)
      : queryKeys.disabled.paymentMethod(),
    queryFn: async () => {
      if (!userId || !paymentMethodId) {
        return null;
      }

      return paymentMethodService.getPaymentMethodById(userId, paymentMethodId);
    },
    enabled: enabled && userId !== null && paymentMethodId !== null && !!tenantHandle,
    // Stable data - payment methods don't change often
    ...cachePresets.stable,
  });

  return {
    ...query,
    paymentMethod: query.data ?? null,
  };
}
